/**
 * Utility functions for submitting forms to Supabase contact-form function
 */

// Your Supabase project ID
const SUPABASE_PROJECT_ID = 'skxqpztdcgnmncooiopw';
const FUNCTION_URL = `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/contact-form`;

interface ContactFormData {
  full_name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  service_type?: string;
  budget_range?: string;
  timeline?: string;
  heard_from?: string;
}

/**
 * Submit form data to Supabase contact form function
 */
export async function submitContactForm(formData: ContactFormData): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Form submission error:', data);
      return { 
        success: false, 
        message: data.error || 'Failed to submit form. Please try again.',
        data
      };
    }
    
    return { 
      success: true, 
      message: 'Form submitted successfully!',
      data
    };
  } catch (error) {
    console.error('Form submission error:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred. Please try again.'
    };
  }
}

/**
 * Submit job application data to Supabase contact form function
 */
export async function submitJobApplication(applicationData: {
  name: string;
  email: string;
  position: string;
  experience: string;
  skills: string;
  motivation: string;
}): Promise<{ success: boolean; message: string; data?: any }> {
  // Transform job application data to contact form format
  const contactFormData: ContactFormData = {
    full_name: applicationData.name,
    email: applicationData.email,
    message: applicationData.motivation,
    service_type: 'Job Application',
    // Additional fields specifically for job applications
    company: `Position: ${applicationData.position}`,
    budget_range: `Experience: ${applicationData.experience}`,
    timeline: `Skills: ${applicationData.skills}`
  };
  
  return submitContactForm(contactFormData);
}

/**
 * Submit project inquiry data to Supabase contact form function
 */
export async function submitProjectInquiry(inquiryData: {
  name: string;
  email: string;
  companyName: string;
  projectType: string;
  timeframe: string;
  budget: string;
  description: string;
}): Promise<{ success: boolean; message: string; data?: any }> {
  // Transform project inquiry data to contact form format
  const contactFormData: ContactFormData = {
    full_name: inquiryData.name,
    email: inquiryData.email,
    company: inquiryData.companyName,
    message: inquiryData.description,
    service_type: inquiryData.projectType,
    budget_range: inquiryData.budget,
    timeline: inquiryData.timeframe
  };
  
  return submitContactForm(contactFormData);
} 